import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Tour } from 'antd';
import { translate } from '../../i18n';
import { FREE_TRIAL_MONTHS, FREE_TRIAL_PARCELS } from '../../config/onboarding';

export const SEEN_KEY = 'didYouKnowTourSeen.v1';
export const OPEN_DELAY_MS = 2500;

/**
 * Infobulles « Le saviez-vous ? », chacune pointée sur le bouton qui réalise l'action proposée
 * (attribut data-tour sur la cible). L'ordre est celui de l'affichage.
 */
export const TIPS = [
    { id: 'free_trial', target: 'account', enabled: () => FREE_TRIAL_MONTHS > 0 },
    { id: 'no_documents', target: 'account' },
    { id: 'soil_insights', target: 'account' },
    { id: 'country', target: 'country' },
    { id: 'language', target: 'language' },
    { id: 'domain', target: 'domain' },
];

const fill = (text, values) => (text || '').replace(/\{(\w+)\}/g, (all, name) => (name in values ? values[name] : all));

/** Cible affichée à l'écran (un bouton masqué sur mobile ne reçoit pas d'infobulle). */
const visibleTarget = (name) => {
    const element = document.querySelector(`[data-tour="${name}"]`);
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? element : null;
};

/** Infobulles dont la cible est visible maintenant. */
export const availableTips = () => TIPS.filter((tip) => (!tip.enabled || tip.enabled()) && visibleTarget(tip.target));

/** Étapes du Tour dans la langue du site. */
export const stepsOf = (tips, texts) =>
    tips.map((tip, index) => ({
        title: texts.title,
        description: fill(texts.tips[tip.id], { months: FREE_TRIAL_MONTHS, parcels: FREE_TRIAL_PARCELS }),
        target: () => document.querySelector(`[data-tour="${tip.target}"]`),
        placement: 'bottom',
        nextButtonProps: { children: index === tips.length - 1 ? texts.finish : texts.next },
        prevButtonProps: { children: texts.previous },
    }));

const seen = () => {
    try {
        return localStorage.getItem(SEEN_KEY) === '1';
    } catch {
        return false;
    }
};

const markSeen = () => {
    try {
        localStorage.setItem(SEEN_KEY, '1');
    } catch {
        // stockage indisponible : la visite pourra se rejouer, sans gravité
    }
};

/**
 * À l'ouverture de l'accueil, pour un visiteur non connecté et une seule fois par navigateur :
 * fait découvrir l'essai gratuit, l'inscription sans documents, les analyses de sols, le choix du
 * pays d'activité, de la langue et du secteur (agricole / pastoral), chaque infobulle pointant le
 * bouton concerné.
 */
const DidYouKnowTour = () => {
    const location = useLocation();
    const language = useSelector((state) => state.lang?.lang) || 'FR';
    const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
    const texts = translate('did_you_know', language) || translate('did_you_know', 'FR');
    const [tips, setTips] = useState([]);
    const [current, setCurrent] = useState(0);
    const eligible = location.pathname === '/' && !token && !seen();

    useEffect(() => {
        if (!eligible) return undefined;
        // Laisse la page se dessiner (en-tête, bannières) avant de pointer les boutons.
        const timer = setTimeout(() => setTips(availableTips()), OPEN_DELAY_MS);
        return () => clearTimeout(timer);
    }, [eligible]);

    if (!eligible || tips.length === 0) return null;

    const close = () => {
        markSeen();
        setTips([]);
    };

    return (
        <Tour
            open
            steps={stepsOf(tips, texts)}
            current={current}
            onChange={setCurrent}
            onClose={close}
            onFinish={close}
            indicatorsRender={(index, total) => `${index + 1} / ${total}`}
            zIndex={1060}
        />
    );
};

export default DidYouKnowTour;
